export const VENUE_DEAL_CATEGORY_HAPPY_HOUR = "happy_hour";
export const VENUE_DEAL_CATEGORY_FOOD_SPECIAL = "food_special";
export const VENUE_DEAL_CATEGORY_DRINK_SPECIAL = "drink_special";
export const VENUE_DEAL_CATEGORY_LIMITED_TIME = "limited_time";
export const VENUE_DEAL_CATEGORY_OTHER = "other";

export type VenueDealCategory =
  | typeof VENUE_DEAL_CATEGORY_HAPPY_HOUR
  | typeof VENUE_DEAL_CATEGORY_FOOD_SPECIAL
  | typeof VENUE_DEAL_CATEGORY_DRINK_SPECIAL
  | typeof VENUE_DEAL_CATEGORY_LIMITED_TIME
  | typeof VENUE_DEAL_CATEGORY_OTHER;

export type VenueDealCategoryLabelContext =
  | "default"
  | "short"
  | "badge";

export type VenueDealCategoryVisualSpec = {
  bgColor: string;
  badgeLabel: string;
  color: string;
  label: string;
  shortLabel: string;
};

export const VENUE_DEAL_CATEGORY_VISUALS = {
  happy_hour: {
    label: "Happy hour",
    shortLabel: "Happy hour",
    badgeLabel: "HAPPY HOUR",
    color: "FBBF24",
    bgColor: "1A1404",
  },
  food_special: {
    label: "Food special",
    shortLabel: "Food",
    badgeLabel: "FOOD SPECIAL",
    color: "EF4E2E",
    bgColor: "2A1208",
  },
  drink_special: {
    label: "Drink special",
    shortLabel: "Drink",
    badgeLabel: "DRINK SPECIAL",
    color: "93C5FD",
    bgColor: "071426",
  },
  limited_time: {
    label: "Limited time",
    shortLabel: "Limited",
    badgeLabel: "LIMITED TIME",
    color: "F87171",
    bgColor: "1A0606",
  },
  other: {
    label: "Other",
    shortLabel: "Other",
    badgeLabel: "DEAL",
    color: "A3A3A3",
    bgColor: "111111",
  },
} as const satisfies Record<VenueDealCategory, VenueDealCategoryVisualSpec>;

export const VENUE_DEAL_CATEGORY_OPTIONS = [
  { value: VENUE_DEAL_CATEGORY_HAPPY_HOUR, label: "Happy hour" },
  { value: VENUE_DEAL_CATEGORY_FOOD_SPECIAL, label: "Food special" },
  { value: VENUE_DEAL_CATEGORY_DRINK_SPECIAL, label: "Drink special" },
  { value: VENUE_DEAL_CATEGORY_LIMITED_TIME, label: "Limited time" },
  { value: VENUE_DEAL_CATEGORY_OTHER, label: "Other" },
] as const;

export const VENUE_DEAL_CATEGORY_FILTER_OPTIONS = [
  { value: VENUE_DEAL_CATEGORY_HAPPY_HOUR, label: "Happy hour" },
  { value: VENUE_DEAL_CATEGORY_FOOD_SPECIAL, label: "Food" },
  { value: VENUE_DEAL_CATEGORY_DRINK_SPECIAL, label: "Drink" },
  { value: VENUE_DEAL_CATEGORY_LIMITED_TIME, label: "Limited" },
  { value: VENUE_DEAL_CATEGORY_OTHER, label: "Other" },
] as const;

export function resolveVenueDealCategory(
  category?: string | null,
): VenueDealCategory | null {
  if (!category) {
    return null;
  }

  return Object.prototype.hasOwnProperty.call(
    VENUE_DEAL_CATEGORY_VISUALS,
    category,
  )
    ? (category as VenueDealCategory)
    : null;
}

export function getVenueDealCategoryVisual(
  category?: string | null,
): VenueDealCategoryVisualSpec {
  const resolvedCategory = resolveVenueDealCategory(category);

  return resolvedCategory
    ? VENUE_DEAL_CATEGORY_VISUALS[resolvedCategory]
    : VENUE_DEAL_CATEGORY_VISUALS.other;
}

export function getVenueDealCategoryLabel(
  category?: string | null,
  context: VenueDealCategoryLabelContext = "default",
) {
  const visual = getVenueDealCategoryVisual(category);

  if (context === "badge") {
    return visual.badgeLabel;
  }

  if (context === "short") {
    return visual.shortLabel;
  }

  return visual.label;
}

export function getVenueDealCategoryColor(category?: string | null) {
  return `#${getVenueDealCategoryVisual(category).color.toLowerCase()}`;
}